<template>
  <div class="service-type-page">
    <div class="page-header">
      <h2 class="page-title">服务类型管理</h2>
      <button @click="openAddModal" class="add-btn">+ 添加服务类型</button>
    </div>

    <div class="filter-bar">
      <div class="search-box">
        <input
          type="text"
          v-model="searchKey"
          placeholder="搜索类型名称或代码..."
          class="search-input"
        />
        <button @click="search" class="search-btn">搜索</button>
        <button @click="resetSearch" class="reset-btn">重置</button>
      </div>
    </div>

    <div class="table-container">
      <table class="service-type-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>图标</th>
            <th>类型名称</th>
            <th>类型代码</th>
            <th>服务描述</th>
            <th>排序</th>
            <th>状态</th>
            <th>创建时间</th>
            <th>更新时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in serviceTypes" :key="item.id">
            <td>{{ item.id }}</td>
            <td>
              <img
                v-if="item.icon"
                :src="item.icon"
                class="icon-img"
                alt="icon"
              />
              <span v-else class="icon-placeholder">-</span>
            </td>
            <td>{{ item.name }}</td>
            <td><code class="code-text">{{ item.code }}</code></td>
            <td class="desc-cell">{{ item.description || '-' }}</td>
            <td>{{ item.sort_order == null ? '-' : item.sort_order }}</td>
            <td>
              <span :class="'status status-' + item.status">
                {{ item.status === 1 ? '启用' : '禁用' }}
              </span>
            </td>
            <td class="time-cell">{{ formatDate(item.created_at) || '-' }}</td>
            <td class="time-cell">{{ formatDate(item.updated_at) || '-' }}</td>
            <td>
              <button @click="openEditModal(item)" class="action-btn edit">编辑</button>
              <button
                @click="toggleStatus(item)"
                class="action-btn"
                :class="item.status === 1 ? 'disable' : 'enable'"
              >
                {{ item.status === 1 ? '禁用' : '启用' }}
              </button>
              <button @click="handleDelete(item)" class="action-btn delete">删除</button>
            </td>
          </tr>
          <tr v-if="serviceTypes.length === 0">
            <td colspan="10" class="empty-cell">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="currentPage--" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页（共 {{ total }} 条）</span>
      <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-btn">下一页</button>
    </div>

    <!-- 新增 / 编辑弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingItem ? '编辑服务类型' : '添加服务类型' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>类型名称 <span class="required">*</span></label>
            <input v-model="form.name" type="text" class="form-control" placeholder="如：代送物品" />
          </div>
          <div class="form-group">
            <label>类型代码 <span class="required">*</span></label>
            <input v-model="form.code" type="text" class="form-control" placeholder="如：DELIVERY" />
          </div>
          <div class="form-group">
            <label>图标 URL</label>
            <input v-model="form.icon" type="text" class="form-control" placeholder="可选，图片地址" />
          </div>
          <div class="form-group">
            <label>服务描述</label>
            <input v-model="form.description" type="text" class="form-control" placeholder="可选" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>排序</label>
              <input v-model.number="form.sort_order" type="number" min="0" class="form-control" placeholder="数字越小越靠前" />
            </div>
            <div class="form-group">
              <label>状态</label>
              <select v-model.number="form.status" class="form-control">
                <option :value="1">启用</option>
                <option :value="0">禁用</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="save" class="btn-save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getServiceTypeList,
  createServiceType,
  updateServiceType,
  deleteServiceType
} from '@/api/serviceType'
import { formatDate } from '@/utils/date'

export default {
  name: 'ServiceType',
  data() {
    return {
      searchKey: '',
      currentPage: 1,
      totalPages: 1,
      total: 0,
      serviceTypes: [],
      showModal: false,
      editingItem: null,
      form: this.emptyForm()
    }
  },
  watch: {
    currentPage() {
      this.loadList()
    }
  },
  mounted() {
    this.loadList()
  },
  methods: {
    formatDate,
    emptyForm() {
      return {
        name: '',
        code: '',
        icon: '',
        description: '',
        sort_order: 0,
        status: 1
      }
    },
    async loadList() {
      try {
        const params = { page: this.currentPage, pageSize: 20 }
        const keyword = this.searchKey.trim()
        if (keyword) {
          params.code = keyword
        }
        const res = await getServiceTypeList(params)
        if (res && res.code === 0 && res.data) {
          const pageData = res.data
          this.serviceTypes = pageData.list || []
          this.total = pageData.total || 0
          this.totalPages = Math.max(1, Math.ceil(this.total / (pageData.pageSize || 20)))
        }
      } catch (error) {
      }
    },
    search() {
      this.currentPage = 1
      this.loadList()
    },
    resetSearch() {
      this.searchKey = ''
      this.currentPage = 1
      this.loadList()
    },
    openAddModal() {
      this.editingItem = null
      this.form = this.emptyForm()
      this.showModal = true
    },
    openEditModal(item) {
      this.editingItem = item
      this.form = {
        name: item.name || '',
        code: item.code || '',
        icon: item.icon || '',
        description: item.description || '',
        sort_order: item.sort_order == null ? 0 : item.sort_order,
        status: item.status == null ? 1 : item.status
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editingItem = null
      this.form = this.emptyForm()
    },
    async save() {
      if (!this.form.name.trim()) {
        alert('请输入类型名称')
        return
      }
      if (!this.form.code.trim()) {
        alert('请输入类型代码')
        return
      }

      // 去掉空值字段
      const payload = {}
      Object.keys(this.form).forEach(key => {
        const v = this.form[key]
        if (v !== '' && v !== null && v !== undefined) {
          payload[key] = v
        }
      })

      try {
        if (this.editingItem) {
          await updateServiceType(this.editingItem.id, payload)
          alert('更新成功')
        } else {
          await createServiceType(payload)
          alert('添加成功')
        }
        this.closeModal()
        this.loadList()
      } catch (error) {
        const msg = error && error.response && error.response.data && error.response.data.message
        alert(msg || '保存失败，请稍后重试')
      }
    },
    async toggleStatus(item) {
      const action = item.status === 1 ? '禁用' : '启用'
      if (confirm(`确定要${action}服务类型 ${item.name} 吗？`)) {
        try {
          const newStatus = item.status === 1 ? 0 : 1
          await updateServiceType(item.id, { status: newStatus })
          item.status = newStatus
        } catch (error) {
          alert('更新状态失败')
        }
      }
    },
    async handleDelete(item) {
      if (confirm(`确定要删除（软删除）服务类型 ${item.name} 吗？记录将保留，可后续恢复。`)) {
        try {
          await deleteServiceType(item.id)
          alert('删除成功')
          this.loadList()
        } catch (error) {
          alert('删除失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.service-type-page {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.filter-bar {
  margin-bottom: 16px;
  padding: 14px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 280px;
}

.search-btn,
.reset-btn {
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.search-btn {
  background: #3498db;
  color: #fff;
}

.reset-btn {
  background: #ecf0f1;
  color: #333;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.service-type-table {
  width: 100%;
  border-collapse: collapse;
}

.service-type-table th,
.service-type-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.service-type-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.icon-img {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
}

.icon-placeholder {
  color: #bbb;
}

.code-text {
  background: #f1f3f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #495057;
}

.desc-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time-cell {
  white-space: nowrap;
  color: #777;
}

.empty-cell {
  text-align: center;
  color: #999;
  padding: 30px 0;
}

.status {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
}

.status-1 { background: #e8f5e9; color: #2ecc71; }
.status-0 { background: #ffebee; color: #e74c3c; }

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
  color: #fff;
}

.action-btn.edit { background: #3498db; }
.action-btn.disable { background: #e74c3c; }
.action-btn.enable { background: #2ecc71; }
.action-btn.delete { background: #95a5a6; }

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 560px;
  max-width: 92%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row .form-group {
  flex: 1;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.required {
  color: #e74c3c;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
